import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import { TestProps } from "shared/types";

import type { ButtonProps } from "features/components/Button";
import TextField from "features/components/TextField";
import Typography from "features/components/Typography";
import ConfirmationModal from "features/ui/ConfirmationModal";

interface Props extends TestProps {
  isOpen: boolean;
  title: string;
  body: ReactNode;
  confirmValue: string;
  typedValue: string;
  setTypedValue: (value: string) => void;
  onClose: (confirmed: boolean) => void;
  isLoading: boolean;
  confirmText?: string;
  confirmColor?: ButtonProps["color"];
  error?: string;
}

const TypeToConfirmModal = ({
  isOpen,
  title,
  body,
  confirmValue,
  typedValue,
  setTypedValue,
  onClose,
  isLoading,
  testId,
  confirmText,
  confirmColor = "error",
  error,
}: Props) => {
  const { t } = useTranslation();

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      text={
        <div className="flex flex-col gap-4">
          {body}
          <Typography variant="body1">
            <Trans
              t={t}
              i18nKey="common:type_to_confirm"
              values={{ value: confirmValue }}
              components={{ bold: <strong /> }}
            />
          </Typography>
          <TextField
            value={typedValue}
            onChange={({ target: { value } }) => setTypedValue(value)}
            placeholder={confirmValue}
            testId={testId}
          />
        </div>
      }
      confirmText={confirmText ?? t("common:delete.action")}
      confirmColor={confirmColor}
      // Pasting the value in brings whitespace with it often enough that a Confirm which stays disabled over a
      // trailing space reads as broken.
      confirmDisabled={typedValue.trim() !== confirmValue}
      loading={isLoading}
      closeDisabled={isLoading}
      error={error}
    />
  );
};

export default TypeToConfirmModal;
